import { useState } from 'react';
import { Copy, Check, Link2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { useToast } from '@/shared/hooks/use-toast';
import { getIntakeUrl } from '@/features/intake/services/intakeApi';

interface IntakeLinkCardProps {
  contractorId: string;
}

export function IntakeLinkCard({ contractorId }: IntakeLinkCardProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const intakeUrl = getIntakeUrl(contractorId);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(intakeUrl);
      setCopied(true);
      toast({ title: 'Link copied!', description: 'Share this link with your customers.' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Link2 className="h-4 w-4 text-primary" />
          Your Public Intake Link
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-3">
          Share this link on your website, Google Business profile, or anywhere customers can find you.
          They can fill in their info and record a video or schedule a call.
        </p>
        <div className="flex gap-2">
          <Input value={intakeUrl} readOnly className="text-xs font-mono" />
          <Button variant="outline" size="icon" onClick={handleCopy} aria-label="Copy link">
            {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
